import { useCallback, useEffect, useRef, useState } from 'react';

import notificationService from '../../domain/services/notification/internal.notification.service';
import notificationSoundService from '../../domain/services/notification/notification.sound.service';
import socketService from '../../domain/services/api/socket';

const HEAD_REFRESH_THROTTLE_MS = 2000;

const initialState = {
    items: [],
    pendingCount: 0,
    pageIndex: 0,
    hasMore: false,
    status: 'loading',
    loadingMore: false,
    loadMoreFailed: false,
};

const withViewedAt = (items, viewedAt, notificationId = null) =>
    items.map(item =>
        (notificationId === null || item.id === notificationId) && !item.viewedAt
            ? { ...item, viewedAt }
            : item
    );

export default function useNotifications() {

    const [state, setState] = useState(initialState);

    const isMountedRef = useRef(true);
    const stateRef = useRef(initialState);
    const lastHeadRefreshRef = useRef(0);
    const isHeadRefreshInFlightRef = useRef(false);

    stateRef.current = state;

    const updateState = useCallback((changes) => {
        if (!isMountedRef.current)
            return;

        setState(current => ({ ...current, ...changes }));
    }, []);

    const loadFirstPage = useCallback(async () => {

        updateState({ status: 'loading' });
        lastHeadRefreshRef.current = Date.now();

        try {
            const page = await notificationService.findMine({ pageIndex: 0 });

            updateState({
                items: page.notifications,
                pendingCount: page.pendingCount,
                pageIndex: page.pageIndex,
                hasMore: page.hasMore,
                status: 'ready',
                loadMoreFailed: false,
            });
        } catch (error) {
            updateState({ status: 'error' });
        }
    }, [updateState]);

    const loadMore = useCallback(async () => {

        const { hasMore, loadingMore, pageIndex, status } = stateRef.current;
        if (!hasMore || loadingMore || status !== 'ready')
            return;

        updateState({ loadingMore: true, loadMoreFailed: false });

        try {
            const page = await notificationService.findMine({ pageIndex: pageIndex + 1 });

            setState(current => ({
                ...current,
                items: notificationService.appendPage(current.items, page.notifications),
                pendingCount: page.pendingCount,
                pageIndex: page.pageIndex,
                hasMore: page.hasMore,
                loadingMore: false,
            }));
        } catch (error) {
            updateState({ loadingMore: false, loadMoreFailed: true });
        }
    }, [updateState]);

    const refreshHead = useCallback(async () => {

        if (isHeadRefreshInFlightRef.current)
            return;

        isHeadRefreshInFlightRef.current = true;

        try {
            const page = await notificationService.findMine({ pageIndex: 0 });

            setState(current => ({
                ...current,
                items: notificationService.mergeHead(current.items, page.notifications),
                pendingCount: page.pendingCount,
                hasMore: current.hasMore || page.hasMore,
                status: 'ready',
            }));
        } catch (error) {
            // silencioso: o head é recarregado na próxima reconexão ou notificação
        } finally {
            lastHeadRefreshRef.current = Date.now();
            isHeadRefreshInFlightRef.current = false;
        }
    }, []);

    const markAllRead = useCallback(async () => {

        const previousItems = stateRef.current.items;
        const previousPendingCount = stateRef.current.pendingCount;

        setState(current => ({
            ...current,
            items: withViewedAt(current.items, new Date().toISOString()),
            pendingCount: 0,
        }));

        try {
            await notificationService.readAll();
        } catch (error) {
            updateState({ items: previousItems, pendingCount: previousPendingCount });
        }
    }, [updateState]);

    const markAsRead = useCallback(async (notificationId) => {

        const previousItems = stateRef.current.items;
        const previousPendingCount = stateRef.current.pendingCount;

        const isAlreadyRead = previousItems.some(item => item.id === notificationId && item.viewedAt);
        if (isAlreadyRead)
            return;

        setState(current => ({
            ...current,
            items: withViewedAt(current.items, new Date().toISOString(), notificationId),
            pendingCount: Math.max(current.pendingCount - 1, 0),
        }));

        try {
            await notificationService.read(notificationId);
        } catch (error) {
            updateState({ items: previousItems, pendingCount: previousPendingCount });
        }
    }, [updateState]);

    const clearAll = useCallback(async () => {

        const previous = stateRef.current;
        if (!previous.items.length)
            return;

        setState(current => ({
            ...current,
            items: [],
            pendingCount: 0,
            pageIndex: 0,
            hasMore: false,
            loadMoreFailed: false,
        }));

        try {
            await notificationService.clearAll();
        } catch (error) {
            updateState({
                items: previous.items,
                pendingCount: previous.pendingCount,
                pageIndex: previous.pageIndex,
                hasMore: previous.hasMore,
            });
        }
    }, [updateState]);

    useEffect(() => {

        isMountedRef.current = true;

        loadFirstPage();

        const onNotificationNew = (payload = {}) => {

            notificationSoundService.play();

            if (!payload.title) {
                refreshHead();
                return;
            }

            setState(current => ({
                ...current,
                items: notificationService.mergeHead(current.items, [notificationService.normalize(payload)]),
                pendingCount: current.pendingCount + 1,
            }));
        }

        const onNotificationRemoved = (payload = {}) => {

            setState(current => ({
                ...current,
                items: notificationService.removeByIds(current.items, payload.ids),
            }));

            refreshHead();
        }

        const onConnect = () => {
            const elapsed = Date.now() - lastHeadRefreshRef.current;
            if (elapsed < HEAD_REFRESH_THROTTLE_MS)
                return;

            refreshHead();
        }

        const unsubscribeNotification = socketService.subscribe('notification:new', onNotificationNew);
        const unsubscribeRemoval = socketService.subscribe('notification:removed', onNotificationRemoved);
        const unsubscribeConnect = socketService.subscribe('connect', onConnect);
        const detachSoundPrime = notificationSoundService.primeOnFirstGesture();

        return () => {
            isMountedRef.current = false;
            unsubscribeNotification();
            unsubscribeRemoval();
            unsubscribeConnect();
            detachSoundPrime();
        };
    }, [loadFirstPage, refreshHead]);

    return {
        ...state,
        loadMore,
        markAllRead,
        markAsRead,
        clearAll,
        retry: loadFirstPage,
    };
}
